import { useMemo, useRef, useState } from "react";
import {
  AlertCircle, BarChart3, Banknote, Loader2, Printer, RefreshCw,
  SlidersHorizontal, Users, XCircle,
} from "lucide-react";

import useDebounce from "../components/useDebounce";
import {
  useGetMembershipDashboardFiltersQuery,
  useGetMembershipDashboardQuery,
  type AnalysisRow,
  type MembershipAnalysis,
} from "../api/membershipApi";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
// Every money figure the API returns is already in naira — the kobo→naira
// conversion happens once, server-side.
const naira = (n: any) =>
  `₦${Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 2 })}`;

const pct = (part: number, total: number) =>
  !total || !part ? "0.0%" : ((part / total) * 100).toFixed(1) + "%";

const EMPTY = {
  state_id: "", chapter_id: "", category_id: "", band_id: "", sector_id: "",
  grade: "", gender: "", year: "", reg_year: "", exam_id: "", event_id: "",
  exclude_deceased: false,
};
type Filters = typeof EMPTY;

/* ------------------------------------------------------------------ */
/* Print                                                                */
/* ------------------------------------------------------------------ */
function printRef(el: HTMLDivElement | null, title: string) {
  if (!el) return;
  const pw = window.open("", "_blank");
  if (!pw) {
    alert("Pop-ups are blocked. Please allow pop-ups for this site to print.");
    return;
  }
  const styles = Array.from(document.head.querySelectorAll("style"))
    .map((s) => `<style>${s.textContent}</style>`).join("\n");
  const links = Array.from(document.head.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
    .map((l) => `<link rel="stylesheet" href="${l.href}">`).join("\n");

  pw.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
${links}${styles}
<style>
  body { background:#fff !important; padding:32px; font-family:sans-serif; }
  @page { margin:1.5cm; }
  table { page-break-inside:auto; border-collapse:collapse; width:100%; }
  tr { page-break-inside:avoid; }
  thead { display:table-header-group; }
  .no-print { display:none !important; }
</style></head><body>${el.innerHTML}</body></html>`);
  pw.document.close();
  pw.focus();
  pw.onload = () => pw.print();
  setTimeout(() => { try { pw.print(); } catch (_) {} }, 900);
}

/* ------------------------------------------------------------------ */
/* Atoms                                                                */
/* ------------------------------------------------------------------ */
function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color: string }) {
  return (
    <div className={`flex-1 min-w-[150px] rounded-2xl p-4 border ${color}`}>
      <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1">{label}</p>
      <p className="text-2xl font-black leading-none break-words">{value}</p>
      {sub && <p className="text-[10px] font-bold opacity-60 mt-1">{sub}</p>}
    </div>
  );
}

function SectionHeader({ num, title, cols }: { num: string; title: string; cols: string[] }) {
  return (
    <>
      <tr>
        <td colSpan={cols.length} className="pt-8 pb-0">
          <div className="flex items-stretch overflow-hidden rounded-t-xl shadow-sm">
            <div className="bg-gray-900 text-white text-[15px] font-black px-5 py-3.5 flex items-center shrink-0 min-w-[56px] justify-center">
              {num}
            </div>
            <div className="bg-gray-800 text-white flex-1 px-5 py-3.5 flex items-center">
              <span className="text-[13px] font-black uppercase tracking-[0.15em]">{title}</span>
            </div>
          </div>
        </td>
      </tr>
      <tr className="bg-gray-100 border-x border-gray-200">
        {cols.map((c, i) => (
          <th key={c + i} className={`px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-gray-500 ${i === 0 ? "text-left" : "text-right"}`}>
            {c}
          </th>
        ))}
      </tr>
    </>
  );
}

function NoDataRow({ span }: { span: number }) {
  return (
    <tr className="border-x border-b border-gray-100 bg-gray-50/30">
      <td colSpan={span} className="px-4 py-5 text-center text-[12px] text-gray-400 italic font-medium">
        No data for this selection
      </td>
    </tr>
  );
}

function SectionEnd({ span }: { span: number }) {
  return (
    <>
      <tr><td colSpan={span} className="border-x border-b border-gray-200 rounded-b-xl h-1 bg-white" /></tr>
      <tr><td colSpan={span} className="h-3" /></tr>
    </>
  );
}

function ShareBar({ value, total }: { value: number; total: number }) {
  const p = total ? (value / total) * 100 : 0;
  const color = p >= 50 ? "bg-indigo-600" : p >= 25 ? "bg-indigo-500" : p >= 10 ? "bg-indigo-400" : "bg-indigo-300";
  return (
    <td className="px-4 py-3">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden min-w-[60px]">
          <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${Math.min(100, p)}%` }} />
        </div>
        <span className="text-[11px] font-black text-gray-500 w-12 text-right shrink-0 tabular-nums">
          {pct(value, total)}
        </span>
      </div>
    </td>
  );
}

/**
 * One breakdown: members, money paid, money owed — on the same row.
 *
 * A headcount by state answers "where are our members"; the two money columns
 * beside it answer "and which of those states is actually paying".
 */
function Breakdown({ num, title, rows, total, limit }: {
  num: string; title: string; rows: AnalysisRow[]; total: number; limit?: number;
}) {
  const shown = limit ? rows.slice(0, limit) : rows;
  const cols = ["Description", "Members", "Paid", "Owed", "Share of members"];
  return (
    <>
      <SectionHeader num={num} title={title} cols={cols} />
      {shown.length === 0 ? <NoDataRow span={cols.length} /> : shown.map((r, i) => (
        <tr key={String(r.key) + r.label + i} className="border-x border-b border-gray-100 even:bg-gray-50/40 hover:bg-indigo-50/30 transition-colors">
          <td className="px-4 py-3 text-[13px] font-bold text-gray-800">{r.label}</td>
          <td className="px-4 py-3 text-[13px] text-right font-black text-gray-900 tabular-nums">{r.count.toLocaleString()}</td>
          <td className="px-4 py-3 text-[13px] text-right font-semibold text-green-700 tabular-nums">{naira(r.paid)}</td>
          <td className={`px-4 py-3 text-[13px] text-right font-semibold tabular-nums ${r.owed > 0 ? "text-red-600" : "text-gray-400"}`}>
            {naira(r.owed)}
          </td>
          <ShareBar value={r.count} total={total} />
        </tr>
      ))}
      {limit && rows.length > limit && (
        <tr className="border-x border-b border-gray-100 bg-gray-50/60">
          <td colSpan={cols.length} className="px-4 py-2 text-[11px] text-gray-400 font-semibold italic">
            Showing the top {limit} of {rows.length.toLocaleString()} — narrow the filters above to see the rest.
          </td>
        </tr>
      )}
      <SectionEnd span={cols.length} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Report                                                               */
/* ------------------------------------------------------------------ */
function Report({ data, title, chips }: {
  data?: MembershipAnalysis;
  title: string;
  chips: Array<{ label: string; value: string }>;
}) {
  const s = data?.summary;
  const f = data?.finance;
  const total = s?.total_members ?? 0;
  const COLS = 5;

  const generatedAt = s?.generated_at
    ? new Date(s.generated_at).toLocaleString("en-GB", {
        day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : new Date().toLocaleString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="font-sans text-gray-900">
      <div className="text-center pb-6 border-b-4 border-gray-900 mb-1">
        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-gray-400 mb-2">
          Nigerian Institute of Quantity Surveyors — NIQS
        </p>
        <h1 className="text-[19px] font-black uppercase tracking-wide text-gray-900 leading-tight">
          Membership Analysis
        </h1>
        <p className="text-[14px] font-bold text-indigo-700 mt-1">{title}</p>
        <p className="text-[10px] text-gray-400 mt-1.5 font-semibold">Generated: {generatedAt}</p>
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2 justify-center">
            {chips.map((c) => (
              <span key={c.label + c.value} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 border border-indigo-100 rounded-full text-[9px] font-bold text-indigo-700 uppercase tracking-wider">
                {c.label}: {c.value}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Headcount */}
      <div className="flex flex-wrap gap-3 my-5">
        <StatCard label="Members" value={total.toLocaleString()} sub={`${(s?.living_members ?? 0).toLocaleString()} living · ${(s?.deceased_members ?? 0).toLocaleString()} deceased`} color="bg-gray-900 text-white border-gray-900" />
        <StatCard label={`New in ${s?.new_members_year ?? ""}`} value={(s?.new_members ?? 0).toLocaleString()} sub="Joined the register" color="bg-blue-50 text-blue-900 border-blue-200" />
        <StatCard label="Owing" value={(s?.members_owing ?? 0).toLocaleString()} sub={pct(s?.members_owing ?? 0, total) + " of members"} color="bg-red-50 text-red-900 border-red-200" />
        <StatCard label="Up To Date" value={(s?.members_clear ?? 0).toLocaleString()} sub={pct(s?.members_clear ?? 0, total) + " of members"} color="bg-green-50 text-green-900 border-green-200" />
      </div>

      {/* Money */}
      <div className="flex flex-wrap gap-3 my-5">
        <StatCard label="Money Paid" value={naira(f?.money_paid)} sub={`Dues ${naira(f?.dues_paid)} · Other ${naira(f?.other_paid)}`} color="bg-green-600 text-white border-green-700" />
        <StatCard label="Money Owed" value={naira(f?.money_owed)} sub="Outstanding dues only" color="bg-red-600 text-white border-red-700" />
        <StatCard label="Dues Billed" value={naira(f?.dues_billed)} sub="Across this selection" color="bg-amber-50 text-amber-900 border-amber-200" />
        <StatCard label="Collection Rate" value={`${f?.collection_rate ?? 0}%`} sub="Dues paid ÷ dues billed" color="bg-indigo-50 text-indigo-900 border-indigo-200" />
      </div>

      <p className="text-[11px] text-gray-400 font-medium italic mb-4">
        “Owed” counts annual dues only. Exam, event, upgrade and firm fees are voluntary — an
        abandoned one is not a debt, so it never appears as money owed, though anything actually
        paid for them is counted under “Money Paid”.
      </p>

      {total === 0 ? (
        <div className="py-20 text-center border-2 border-dashed border-gray-200 rounded-2xl">
          <p className="font-bold text-gray-700">No members match these filters</p>
          <p className="text-sm text-gray-400 mt-1">Widen the selection above.</p>
        </div>
      ) : (
        <table className="w-full border-collapse">
          <tbody>
            <SectionHeader num="1" title="Money by Source" cols={["Source", "Invoices", "Billed", "Paid", "Balance"]} />
            {(data?.finance_by_source ?? []).length === 0 ? <NoDataRow span={COLS} /> : (data?.finance_by_source ?? []).map((r, i) => (
              <tr key={String(r.source) + i} className="border-x border-b border-gray-100 even:bg-gray-50/40 hover:bg-indigo-50/30 transition-colors">
                <td className="px-4 py-3 text-[13px] font-bold text-gray-800">
                  {r.label}
                  {!r.is_dues && (
                    <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                      not dues
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-[13px] text-right font-black text-gray-900 tabular-nums">{r.count.toLocaleString()}</td>
                <td className="px-4 py-3 text-[13px] text-right font-semibold text-gray-700 tabular-nums">{naira(r.billed)}</td>
                <td className="px-4 py-3 text-[13px] text-right font-semibold text-green-700 tabular-nums">{naira(r.paid)}</td>
                <td className={`px-4 py-3 text-[13px] text-right font-semibold tabular-nums ${r.balance > 0 ? "text-red-600" : "text-gray-400"}`}>
                  {naira(r.balance)}
                </td>
              </tr>
            ))}
            <SectionEnd span={COLS} />

            <Breakdown num="2" title="Members by Category" rows={data?.by_category ?? []} total={total} />
            <Breakdown num="3" title="Members by Grade"    rows={data?.by_grade ?? []}    total={total} />
            <Breakdown num="4" title="Members by State"    rows={data?.by_state ?? []}    total={total} />
            <Breakdown num="5" title="Members by Chapter"  rows={data?.by_chapter ?? []}  total={total} limit={25} />
            <Breakdown num="6" title="Members by Band"     rows={data?.by_band ?? []}     total={total} />
            <Breakdown num="7" title="Members by Employment Sector" rows={data?.by_sector ?? []} total={total} />
            <Breakdown num="8" title="Members by Gender"   rows={data?.by_gender ?? []}   total={total} />

            <SectionHeader num="9" title="Members Joined by Year" cols={["Year", "Members", "", "", "Share of members"]} />
            {(data?.by_join_year ?? []).length === 0 ? <NoDataRow span={COLS} /> : (data?.by_join_year ?? []).map((r) => (
              <tr key={r.label} className="border-x border-b border-gray-100 even:bg-gray-50/40 hover:bg-indigo-50/30 transition-colors">
                <td className="px-4 py-3 text-[13px] font-bold text-gray-800">{r.label}</td>
                <td className="px-4 py-3 text-[13px] text-right font-black text-gray-900 tabular-nums">{r.count.toLocaleString()}</td>
                <td colSpan={2} />
                <ShareBar value={r.count} total={total} />
              </tr>
            ))}
            <SectionEnd span={COLS} />

            <SectionHeader num="10" title="Largest Outstanding Balances" cols={["Member", "Membership No.", "Grade", "Invoices", "Owed"]} />
            {(data?.top_debtors ?? []).length === 0 ? (
              <tr className="border-x border-b border-gray-100 bg-green-50/50">
                <td colSpan={COLS} className="px-4 py-4 text-center text-[12px] font-bold text-green-700">
                  No outstanding dues in this selection.
                </td>
              </tr>
            ) : (data?.top_debtors ?? []).map((r) => (
              <tr key={r.member_id} className="border-x border-b border-gray-100 even:bg-gray-50/40 hover:bg-indigo-50/30 transition-colors">
                <td className="px-4 py-3 text-[13px] font-bold text-gray-800">{r.name}</td>
                <td className="px-4 py-3 text-[13px] text-right font-medium text-gray-600 tabular-nums">{r.membership_no || "—"}</td>
                <td className="px-4 py-3 text-[13px] text-right font-medium text-gray-600">{r.grade}</td>
                <td className="px-4 py-3 text-[13px] text-right font-medium text-gray-600 tabular-nums">{r.invoices}</td>
                <td className="px-4 py-3 text-[13px] text-right font-black text-red-600 tabular-nums">{naira(r.owed)}</td>
              </tr>
            ))}
            <SectionEnd span={COLS} />

            <SectionHeader num="11" title="Participation" cols={["Activity", "Members", "", "", "Share of members"]} />
            {[
              ["Applied for at least one exam", data?.participation?.members_with_exam_applications ?? 0],
              ["Registered for at least one event", data?.participation?.members_with_event_registrations ?? 0],
            ].map(([label, count]) => (
              <tr key={label as string} className="border-x border-b border-gray-100 even:bg-gray-50/40">
                <td className="px-4 py-3 text-[13px] font-bold text-gray-800">{label}</td>
                <td className="px-4 py-3 text-[13px] text-right font-black text-gray-900 tabular-nums">{(count as number).toLocaleString()}</td>
                <td colSpan={2} />
                <ShareBar value={count as number} total={total} />
              </tr>
            ))}
            <SectionEnd span={COLS} />
          </tbody>
        </table>
      )}

      <div className="mt-10 pt-4 border-t-2 border-gray-300 text-center space-y-1.5">
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
          Nigerian Institute of Quantity Surveyors (NIQS) — Confidential
        </p>
        <p className="text-[10px] text-gray-300 font-medium">
          Generated from the NIQS Portal and intended solely for official use.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */
export default function MembershipDashboardPage() {
  const printContentRef = useRef<HTMLDivElement>(null);
  const [filters, setFilters] = useState<Filters>({ ...EMPTY });

  // Debounced before it reaches the query. Changing three selects in a row
  // should cost ONE report, not three — the report is the expensive thing on
  // this screen and there is no reason to build the two nobody will read.
  const applied = useDebounce(filters, 600) as Filters;

  const { data: opts } = useGetMembershipDashboardFiltersQuery();
  const { data, isLoading, isFetching, error, refetch } = useGetMembershipDashboardQuery({
    state_id: applied.state_id || undefined,
    chapter_id: applied.chapter_id || undefined,
    category_id: applied.category_id || undefined,
    band_id: applied.band_id || undefined,
    sector_id: applied.sector_id || undefined,
    grade: applied.grade || undefined,
    gender: applied.gender || undefined,
    year: applied.year || undefined,
    reg_year: applied.reg_year || undefined,
    exam_id: applied.exam_id || undefined,
    event_id: applied.event_id || undefined,
    exclude_deceased: applied.exclude_deceased || undefined,
  });

  const set = (k: keyof Filters) => (v: string | boolean) =>
    setFilters((f) => ({ ...f, [k]: v }));

  const chips = useMemo(() => {
    const out: Array<{ label: string; value: string }> = [];
    const named = (list: any[] | undefined, id: string, label: string) => {
      const hit = list?.find((x) => String(x.id) === id);
      if (hit) out.push({ label, value: hit.name ?? hit.title });
    };
    named(opts?.states, filters.state_id, "State");
    named(opts?.chapters, filters.chapter_id, "Chapter");
    named(opts?.categories, filters.category_id, "Category");
    named(opts?.bands, filters.band_id, "Band");
    named(opts?.sectors, filters.sector_id, "Sector");
    named(opts?.exams as any, filters.exam_id, "Exam");
    named(opts?.events as any, filters.event_id, "Event");
    if (filters.grade) out.push({ label: "Grade", value: filters.grade });
    if (filters.gender) out.push({ label: "Gender", value: filters.gender });
    if (filters.year) out.push({ label: "Billing year", value: filters.year });
    if (filters.reg_year) out.push({ label: "Joined", value: filters.reg_year });
    if (filters.exclude_deceased) out.push({ label: "Deceased", value: "Excluded" });
    return out;
  }, [filters, opts]);

  const reportTitle = chips.length === 0
    ? "Whole Membership — All States, All Years"
    : chips.map((c) => c.value).join(" · ");

  const activeCount = chips.length;
  const pending = JSON.stringify(filters) !== JSON.stringify(applied);
  const tooLarge = data?.code === "SELECTION_TOO_LARGE";

  const selectCls = (active: boolean) =>
    `text-[11px] font-semibold border rounded-lg px-2.5 py-1.5 outline-none transition-all cursor-pointer ${
      active ? "border-indigo-400 text-indigo-700 bg-indigo-50"
             : "border-gray-200 text-gray-600 bg-white hover:border-gray-400"
    }`;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 no-print">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-black tracking-tight text-gray-900">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            Membership Analysis
          </h1>
          <p className="text-xs text-gray-500 font-medium mt-0.5">
            {reportTitle}
            {activeCount > 0 && (
              <span className="text-indigo-600 font-bold"> · {activeCount} filter{activeCount > 1 ? "s" : ""}</span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            title="Refresh"
            className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 text-gray-600 ${isFetching ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => printRef(printContentRef.current, reportTitle)}
            disabled={isLoading || !!error || tooLarge}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-black uppercase tracking-widest hover:bg-indigo-500 disabled:opacity-50 transition-colors"
          >
            <Printer className="w-4 h-4" /> Print Report
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden no-print">
        <div className="flex flex-wrap items-center gap-2.5 px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-1.5 text-gray-500 shrink-0">
            <Users className="w-3.5 h-3.5" />
            <span className="text-[10px] font-black uppercase tracking-widest">Who</span>
          </div>

          <select value={filters.state_id} onChange={(e) => set("state_id")(e.target.value)} className={selectCls(!!filters.state_id)}>
            <option value="">All States</option>
            {opts?.states?.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
          </select>

          <select value={filters.chapter_id} onChange={(e) => set("chapter_id")(e.target.value)} className={selectCls(!!filters.chapter_id)}>
            <option value="">All Chapters</option>
            {opts?.chapters?.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
          </select>

          <select value={filters.category_id} onChange={(e) => set("category_id")(e.target.value)} className={selectCls(!!filters.category_id)}>
            <option value="">All Categories</option>
            {opts?.categories?.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
          </select>

          <select value={filters.grade} onChange={(e) => set("grade")(e.target.value)} className={selectCls(!!filters.grade)}>
            <option value="">All Grades</option>
            {opts?.grades?.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>

          <select value={filters.band_id} onChange={(e) => set("band_id")(e.target.value)} className={selectCls(!!filters.band_id)}>
            <option value="">All Bands</option>
            {opts?.bands?.map((b) => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
          </select>

          <select value={filters.sector_id} onChange={(e) => set("sector_id")(e.target.value)} className={selectCls(!!filters.sector_id)}>
            <option value="">All Sectors</option>
            {opts?.sectors?.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
          </select>

          <select value={filters.gender} onChange={(e) => set("gender")(e.target.value)} className={selectCls(!!filters.gender)}>
            <option value="">All Genders</option>
            {opts?.genders?.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 px-5 py-3 bg-gray-50/80">
          <div className="flex items-center gap-1.5 text-gray-500 shrink-0">
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span className="text-[10px] font-black uppercase tracking-widest">Scope</span>
          </div>

          <label className="flex items-center gap-1.5">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Billing year</span>
            <select value={filters.year} onChange={(e) => set("year")(e.target.value)} className={selectCls(!!filters.year)}>
              <option value="">All years</option>
              {opts?.years?.map((y) => <option key={y} value={String(y)}>{y}</option>)}
            </select>
          </label>

          <label className="flex items-center gap-1.5">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Joined</span>
            <select value={filters.reg_year} onChange={(e) => set("reg_year")(e.target.value)} className={selectCls(!!filters.reg_year)}>
              <option value="">Any year</option>
              {opts?.years?.map((y) => <option key={y} value={String(y)}>{y}</option>)}
            </select>
          </label>

          <select value={filters.exam_id} onChange={(e) => set("exam_id")(e.target.value)} className={selectCls(!!filters.exam_id)}>
            <option value="">Any / no exam</option>
            {opts?.exams?.map((e) => <option key={e.id} value={String(e.id)}>{e.title}</option>)}
          </select>

          <select value={filters.event_id} onChange={(e) => set("event_id")(e.target.value)} className={selectCls(!!filters.event_id)}>
            <option value="">Any / no event</option>
            {opts?.events?.map((e) => (
              <option key={e.id} value={String(e.id)}>{e.title}{e.year ? ` (${e.year})` : ""}</option>
            ))}
          </select>

          <label className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer transition-all ${
            filters.exclude_deceased ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-gray-200 bg-white text-gray-600 hover:border-gray-400"
          }`}>
            <input
              type="checkbox"
              checked={filters.exclude_deceased}
              onChange={(e) => set("exclude_deceased")(e.target.checked)}
              className="w-3 h-3 accent-indigo-600"
            />
            <span className="text-[11px] font-semibold">Exclude deceased</span>
          </label>

          {activeCount > 0 && (
            <button
              onClick={() => setFilters({ ...EMPTY })}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-600 text-[10px] font-black uppercase tracking-wider hover:bg-red-100 transition-all"
            >
              <XCircle className="w-3.5 h-3.5" />
              Clear {activeCount} filter{activeCount > 1 ? "s" : ""}
            </button>
          )}
        </div>
      </div>

      {/* Report */}
      <div className="rounded-2xl border border-gray-200 bg-white px-5 py-6 md:px-8 overflow-x-auto">
        {(pending || (isFetching && !isLoading)) && (
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-indigo-50 rounded-lg border border-indigo-100 no-print">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500" />
            <span className="text-[11px] font-semibold text-indigo-600">
              {pending ? "Waiting for you to finish choosing…" : "Applying filters…"}
            </span>
          </div>
        )}

        {isLoading ? (
          <div className="py-32 flex flex-col items-center gap-4 text-gray-400">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-500" />
            <p className="text-sm font-semibold">Generating membership analysis…</p>
            <p className="text-xs text-gray-300 flex items-center gap-1.5">
              <Banknote className="w-3.5 h-3.5" /> Aggregating members, dues and payments
            </p>
          </div>
        ) : tooLarge ? (
          <div className="py-20 text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-amber-500" />
            </div>
            <p className="font-bold text-gray-900">Selection too large</p>
            <p className="text-sm text-gray-500 max-w-md mx-auto">{data?.error}</p>
          </div>
        ) : error ? (
          <div className="py-24 text-center space-y-5">
            <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <AlertCircle className="w-10 h-10 text-red-400" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-lg">Could not load analysis</p>
              <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                {(error as any)?.data?.message || (error as any)?.data?.error
                  || "The server could not generate the report. Please try again."}
              </p>
            </div>
            <button onClick={() => refetch()} className="px-6 py-3 rounded-xl bg-gray-900 text-white font-bold hover:bg-gray-800 transition-colors">
              Retry
            </button>
          </div>
        ) : (
          <div ref={printContentRef}>
            <Report data={data} title={reportTitle} chips={chips} />
          </div>
        )}
      </div>
    </div>
  );
}
